/**
 * Tetromino definitions and rotation states.
 *
 * Coordinate system (shared by the whole of `core/`):
 *   x grows to the right, y grows DOWNWARD. (0,0) is the top-left of the
 *   full 10x40 board, which includes the 20 hidden buffer rows.
 *
 * Each piece is defined by a square bounding box (2 for O, 3 for JLSTZ, 4 for
 * I) and a base occupancy matrix. The other three rotation states are derived
 * by rotating that matrix clockwise, which is exactly what SRS specifies.
 */

export type PieceType = 'I' | 'J' | 'L' | 'O' | 'S' | 'T' | 'Z';

export const PIECE_TYPES: readonly PieceType[] = ['I', 'J', 'L', 'O', 'S', 'T', 'Z'];

/** 0 = spawn, 1 = one CW turn (R), 2 = 180, 3 = one CCW turn (L). */
export type Rotation = 0 | 1 | 2 | 3;

export interface Vec {
  x: number;
  y: number;
}

/**
 * Tetromino colours. Used by the render layer only. Six of the seven are
 * sampled from the block-theme reference art; T's violet is the added hue.
 */
export const PIECE_COLORS: Record<PieceType, string> = {
  I: '#63c9d8',
  J: '#4479bd',
  L: '#e95f2e',
  O: '#efdc38',
  S: '#92c745',
  T: '#8c54d0',
  Z: '#e72f6f',
};

/**
 * Base (rotation 0) occupancy per piece, drawn as a square matrix.
 * '#' = filled. Rows are top-to-bottom, so index = y.
 */
const BASE_MATRIX: Record<PieceType, string[]> = {
  I: [
    '....',
    '####',
    '....',
    '....',
  ],
  J: [
    '#..',
    '###',
    '...',
  ],
  L: [
    '..#',
    '###',
    '...',
  ],
  O: [
    '##',
    '##',
  ],
  S: [
    '.##',
    '##.',
    '...',
  ],
  T: [
    '.#.',
    '###',
    '...',
  ],
  Z: [
    '##.',
    '.##',
    '...',
  ],
};

export const BOX_SIZE: Record<PieceType, number> = {
  I: 4,
  J: 3,
  L: 3,
  O: 2,
  S: 3,
  T: 3,
  Z: 3,
};

function matrixToCells(matrix: string[]): Vec[] {
  const cells: Vec[] = [];
  for (let y = 0; y < matrix.length; y++) {
    const row = matrix[y];
    for (let x = 0; x < row.length; x++) {
      if (row[x] === '#') cells.push({ x, y });
    }
  }
  return cells;
}

/** Rotate a cell inside an NxN box one quarter-turn clockwise. */
function rotateCellCW(cell: Vec, n: number): Vec {
  return { x: n - 1 - cell.y, y: cell.x };
}

function buildRotations(type: PieceType): Vec[][] {
  const n = BOX_SIZE[type];
  const states: Vec[][] = [];
  let current = matrixToCells(BASE_MATRIX[type]);
  for (let r = 0; r < 4; r++) {
    // Sort for stable ordering so equality checks in tests are simple.
    states.push([...current].sort((a, b) => a.y - b.y || a.x - b.x));
    current = current.map((c) => rotateCellCW(c, n));
  }
  return states;
}

/** `SHAPES[type][rotation]` -> cells relative to the piece's box origin. */
export const SHAPES: Record<PieceType, Vec[][]> = {
  I: buildRotations('I'),
  J: buildRotations('J'),
  L: buildRotations('L'),
  O: buildRotations('O'),
  S: buildRotations('S'),
  T: buildRotations('T'),
  Z: buildRotations('Z'),
};

/**
 * Spawn position of the box origin, chosen so every piece's lowest cells land
 * on the last hidden buffer row (y = 19) straddling the two columns left of
 * centre.
 */
export const SPAWN_ORIGIN: Record<PieceType, Vec> = {
  I: { x: 3, y: 18 },
  J: { x: 3, y: 18 },
  L: { x: 3, y: 18 },
  O: { x: 4, y: 18 },
  S: { x: 3, y: 18 },
  T: { x: 3, y: 18 },
  Z: { x: 3, y: 18 },
};

export interface ActivePiece {
  type: PieceType;
  rotation: Rotation;
  /** Board position of the bounding-box origin (top-left of the box). */
  x: number;
  y: number;
}

export function spawnPiece(type: PieceType): ActivePiece {
  const origin = SPAWN_ORIGIN[type];
  return { type, rotation: 0, x: origin.x, y: origin.y };
}

/** Absolute board cells occupied by a piece. */
export function pieceCells(piece: ActivePiece): Vec[] {
  return SHAPES[piece.type][piece.rotation].map((c) => ({
    x: piece.x + c.x,
    y: piece.y + c.y,
  }));
}

/** Cells a piece would occupy at a hypothetical position / rotation. */
export function cellsAt(
  type: PieceType,
  rotation: Rotation,
  x: number,
  y: number,
): Vec[] {
  return SHAPES[type][rotation].map((c) => ({ x: x + c.x, y: y + c.y }));
}

export function rotateIndex(rotation: Rotation, quarterTurns: number): Rotation {
  return (((rotation + quarterTurns) % 4) + 4) % 4 as Rotation;
}

/**
 * Board position of the T piece's centre cell. Only meaningful for T; used by
 * the three-corner T-spin rule.
 */
export function tCenter(piece: ActivePiece): Vec {
  return { x: piece.x + 1, y: piece.y + 1 };
}
